import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

//helpers:
//helpers:
import {getTranslatedGroupName, hasValidFormat} from "../../../../../_metronic/_helpers";

//actions:
import {
    executeInvitaions,
    fetchGroups,
    selectAllGroups,
    selectInvitations,
    setModifiedEmails,
    validateNewEmails,
} from "../../../../redux_slices/settingsCredentialsSlice";

//components:
import Fade from "@material-ui/core/Fade";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import HighlightOffRoundedIcon from "@material-ui/icons/HighlightOffRounded";
import {CircularProgress} from "@material-ui/core";
import {Dropdown} from "react-bootstrap";
import {CustomTooltip, ReusableTable} from "../../../../widgets";
import TextAreaInput from "./TextAreaInput";
import Swal from "sweetalert2";
import useTableDetails from "./useEmailsTable";

const InviteModal = ({showModal, setShowModal}) => {
    const {t} = useTranslation();
    const {formatRow} = useTableDetails();

    //redux:
    const dispatch = useDispatch();
    const allGroups = useSelector(selectAllGroups);
    const {
        modified = [],
        status = {
            type: "idle",
            error: {text: ""},
        },
    } = useSelector(selectInvitations);

    //local state:
    const [page, setPage] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRole, setSelectedRole] = useState({});
    const [emailsToValidate, setEmailsToValidate] = useState([]);
    const [errorTextArea, setErrorTextArea] = useState("");
    const [disabledNext1, setDisabledNext1] = useState(true);

    const validEmails = modified?.filter(email => email.valid);
    const inValidEmails = modified?.filter(email => !email.valid);

    const reset = () => {
        setShowModal(false);
        setTimeout(() => {
            setPage(0);
            setEmailsToValidate([]);
            dispatch(setModifiedEmails([]));
            setSelectedRole(allGroups?.[0] || {});
            setIsLoading(false);
        }, 800);
    };

    useEffect(() => {
        dispatch(fetchGroups());
    }, []);
    useEffect(() => {
        if (page === 1 && !modified.length) setPage(0);
    }, [modified]);
    useEffect(() => {
        setSelectedRole(allGroups?.[0] || {});
    }, [allGroups]);

    useEffect(() => {
        const loading = status === "idle" || status === "loading";
        setIsLoading(loading);
    }, [status]);

    const checkEmailsFormat = value => {
        if (value) {
            const allDetected = value.split(",");
            const emailsWithLength = allDetected?.filter(string => string.length);
            const uniqueEmails = [...new Set(emailsWithLength)];
            const noValidEmails = uniqueEmails.filter(email => !hasValidFormat(email));

            setErrorTextArea(
                noValidEmails.length ? `${t("ADMINPANEL.USERS.WRONG_EMAIL_FORMAT")}: ${noValidEmails.join(", ")}` : ""
            );

            setDisabledNext1(!!noValidEmails.length);
            setEmailsToValidate(!!noValidEmails.length ? [] : uniqueEmails);
        } else {
            setErrorTextArea("");
            setDisabledNext1(true);
        }
    };

    const validateEmails = async () => {
        try {
            const {
                payload: {data, message = [{text: ""}]},
                error = {},
            } = await dispatch(validateNewEmails(emailsToValidate));
            if (data && data.emails) {
                setPage(1);
                dispatch(setModifiedEmails(data.emails.map(em => ({...em, group: selectedRole}))));
            } else if (error?.message || message?.[0]?.text)
                Swal.fire({
                    icon: "error",
                    title: t("MODAL.INVITE_USERS.INVITED.FAIL"),
                    text: error.message || message?.[0]?.text,
                });
        } catch (e) {
            Swal.fire({
                icon: "error",
                title: t("MODAL.INVITE_USERS.INVITED.FAIL"),
                text: e.message,
            });
        }
    };

    const inviteUsers = async () => {
        try {
            let addGroupToEmails = [];
            modified.forEach(email => {
                if (email.valid) {
                    addGroupToEmails.push({email: email.email, group_id: email.group.group_id || 1});
                }
            });
            const {
                payload: {data, message = [{text: ""}]},
                error = {},
            } = await dispatch(executeInvitaions(addGroupToEmails));

            if (data?.length) {
                reset();
                Swal.fire({
                    icon: "success",
                    title: t("MODAL.INVITE_USERS.INVITED"),
                });
            } else if (error?.message || message?.[0]?.text)
                Swal.fire({
                    icon: "error",
                    title: t("MODAL.INVITE_USERS.INVITED.FAIL"),
                    text: error.message || message?.[0]?.text,
                });
        } catch (e) {
            Swal.fire({
                icon: "error",
                title: t("MODAL.INVITE_USERS.INVITED.FAIL"),
                text: e.message,
            });
        }
    };

    const getPageContent = () => {
        if (page === 0)
            return (
                <>
                    <div className="d-flex w-100 gutter-b">
                        <TextAreaInput
                            className="w-100"
                            labelClassName="text-muted font-weight-bolder"
                            langTitle={t("MODAL.INVITE_USERS.TEXTAREA.LABEL")}
                            langPlaceholder={t("MODAL.INVITE_USERS.TEXTAREA.PLACEHOLDER")}
                            defaultValue={emailsToValidate.join(",")}
                            errorMessage={errorTextArea}
                            onChange={checkEmailsFormat}
                            disabled={disabledNext1}
                            rows={10}
                        />
                    </div>

                    <div className="d-flex w-100 flex-column gutter-b">
                        <label className="text-muted font-weight-bolder" htmlFor="role">
                            {t("MODAL.INVITE_USERS.DROPDOWN.LABEL")}
                        </label>

                        <Dropdown className="d-flex w-100" id="role">
                            <CustomTooltip
                                config={{
                                    text: t("ADMINPANEL.USERS.TYPE.SELECTOR.TOOLTIP"),
                                    placement: "left",
                                }}
                            >
                                <Dropdown.Toggle variant="outline-light" className="w-100">
                                    {getTranslatedGroupName(t, selectedRole)} <KeyboardArrowDownIcon/>
                                </Dropdown.Toggle>
                            </CustomTooltip>

                            <Dropdown.Menu className="w-100">
                                {allGroups?.length ? (
                                    allGroups.map((group, i) => (
                                        <Dropdown.Item
                                            key={i}
                                            className="dropdown-item elipsis_1line"
                                            onClick={() => setSelectedRole(group)}
                                            active={group.group_id === selectedRole.group_id}
                                        >
                                            {getTranslatedGroupName(t, group)}
                                        </Dropdown.Item>
                                    ))
                                ) : (
                                    <span>{t("MODAL.INVITE_USERS.DROPDOWN.PLACEHOLDER")}</span>
                                )}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>

                    <div className="d-flex w-100 align-items-center justify-content-around mt-10">
                        <button className="btn btn-light" onClick={reset}>
                            {t("GENERAL.CANCEL")}
                        </button>
                        <button className="btn btn-primary" disabled={disabledNext1} onClick={validateEmails}>
                            {t("MODAL.INVITE_USERS.BUTTONS.REVIEW")}
                        </button>
                    </div>
                </>
            );
        else if (page === 1) {
            return (
                <div className="d-flex flex-column">
                    <h6>{t("MODAL.INVITE_USERS.NEEDS_ATENTION")}</h6>
                    <ReusableTable
                        filteredRows={inValidEmails}
                        emptyMessage={t("MODAL.INVITE_USERS.EMAILS_TABLE.NOT_FOUND")}
                        formatRow={formatRow}
                        isLoading={isLoading}
                        pagination={inValidEmails?.length >= 5}
                    />
                    <div className="d-flex align-items-center gutter-t">
                        <h6 className="m-0 mr-4">{t("MODAL.INVITE_USERS.NEW_USERS")}</h6>
                        <span className="btn btn-sm btn-primary">{validEmails.length}</span>
                    </div>
                    <ReusableTable
                        filteredRows={validEmails}
                        emptyMessage={t("MODAL.INVITE_USERS.EMAILS_TABLE.NOT_FOUND")}
                        formatRow={formatRow}
                        isLoading={isLoading}
                        pagination={validEmails?.length >= 5}
                    />
                    <div className="alert alert-light text-dark w-fit-content mt-8 mb-12">
                        {t("MODAL.INVITE_USERS.COSTS_WARNING")}
                    </div>
                    <div className="d-flex w-100 align-items-center justify-content-around">
                        <button className="btn btn-light" onClick={() => setPage(0)}>
                            {t("GENERAL.BACK")}
                        </button>
                        <button className="btn btn-primary" onClick={inviteUsers} disabled={!validEmails.length}>
                            {t("MODAL.INVITE_USERS.BUTTONS.INVITE", {num: validEmails?.length})}
                        </button>
                    </div>
                </div>
            );
        }

        return <div/>;
    };

    return (
        <Fade
            in={showModal}
            mountOnEnter={false}
            unmountOnExit={false}
            timeout={{
                appear: 1000,
                enter: 1000,
                exit: 500,
            }}
        >
            <div className="invite_users_modal">
                <div className="dark_overlay_modal" onClick={reset}/>
                <div className="card">

                    <div className="card-header border-bottom border-gray">
                        <div className="title">{t("MODAL.INVITE_USERS.TITLE")}</div>
                        <button
                            type="button"
                            className="close ml-auto font-size-base"
                            data-dismiss="modal"
                            aria-label="Close"
                            onClick={reset}
                        >
                            <HighlightOffRoundedIcon fontSize="large"/>
                        </button>
                    </div>

                    <div className="card-body d-flex flex-column">
                        {isLoading ? <CircularProgress/> : getPageContent()}
                    </div>
                </div>
            </div>
        </Fade>
    );
};

export default InviteModal;
